'use client';
import * as React from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import type { SlotComponentPropsFromProps } from '@mui/x-internals/types';
import { ChartsLoadingOverlay } from './ChartsLoadingOverlay';
import { useSeries } from '../hooks/useSeries';
import type { SeriesId } from '../models/seriesType/common';
import { ChartsNoDataOverlay } from './ChartsNoDataOverlay';
import type {
  LoadingOverlayPropsOverrides,
  NoDataOverlayPropsOverrides,
} from '../models/chartsSlotsComponentsProps';

export function useNoData() {
  const seriesPerType = useSeries();

  return Object.values(seriesPerType).every((seriesOfGivenType) => {
    if (!seriesOfGivenType) {
      return true;
    }
    const { series, seriesOrder } = seriesOfGivenType;

    return seriesOrder.every((seriesId: SeriesId) => {
      const seriesItem = series[seriesId];

      // These prevent a type error when building the package.
      // @ts-ignore, sankey type is not declared in the base package
      if (seriesItem.type === 'sankey') {
        // @ts-ignore, sankey type is not declared in the base package
        return seriesItem.data.links.length === 0;
      }

      return seriesItem.data.length === 0;
    });
  });
}

export type CommonOverlayProps = React.SVGAttributes<SVGTextElement> & {
  sx?: SxProps<Theme>;
};

export interface ChartsOverlaySlots {
  /**
   * Overlay component rendered when the chart is in a loading state.
   * @default ChartsLoadingOverlay
   */
  loadingOverlay?: React.ElementType<CommonOverlayProps & LoadingOverlayPropsOverrides>;
  /**
   * Overlay component rendered when the chart has no data to display.
   * @default ChartsNoDataOverlay
   */
  noDataOverlay?: React.ElementType<CommonOverlayProps & NoDataOverlayPropsOverrides>;
}
export interface ChartsOverlaySlotProps {
  loadingOverlay?: SlotComponentPropsFromProps<
    CommonOverlayProps,
    LoadingOverlayPropsOverrides,
    {}
  >;
  noDataOverlay?: SlotComponentPropsFromProps<CommonOverlayProps, NoDataOverlayPropsOverrides, {}>;
}

export interface ChartsOverlayProps {
  /**
   * If `true`, a loading overlay is displayed.
   * @default false
   */
  loading?: boolean;
  /**
   * Overridable component slots.
   * @default {}
   */
  slots?: ChartsOverlaySlots;
  /**
   * The props used for each component slot.
   * @default {}
   */
  slotProps?: ChartsOverlaySlotProps;
}

export function ChartsOverlay(props: ChartsOverlayProps) {
  const noData = useNoData();

  if (props.loading) {
    const LoadingOverlay = props.slots?.loadingOverlay ?? ChartsLoadingOverlay;
    return <LoadingOverlay {...props.slotProps?.loadingOverlay} />;
  }
  if (noData) {
    const NoDataOverlay = props.slots?.noDataOverlay ?? ChartsNoDataOverlay;
    return <NoDataOverlay {...props.slotProps?.noDataOverlay} />;
  }
  return null;
}
